import { WIL_AVATAR } from '@src/components/sco/Advisors/constants'
import { conversionCheckoutHref } from '@src/components/sco/Conversion/controller/checkoutParams'
import { canProrateUpgradeToProduct } from '@src/components/sco/UpgradeForm'
import { useCurrentUser } from '@src/hooks/useCurrentUser'
import { usePathname, useSearchParams } from 'next/navigation'
import { useMemo } from 'react'

/**
 * Advisors access policy.
 *
 * | Tier | Wil (AI) | Marketplace advisors | Human team advisors ($299) |
 * |------|----------|----------------------|----------------------------|
 * | Guest (logged out) | Landing + email capture | Paywalled (`canAccessChat`) | Landing merchandising |
 * | Free registered | {@link FREE_WIL_MESSAGE_LIMIT} messages total | Paywalled (`canAccessChat`, $49) | Shown, gated behind $299 |
 * | $49 Tools (and other paid chat tiers) | Unlimited AI | Message + apply to seats | Shown, gated behind $299 |
 * | $299 Advisor | Unlimited AI | Message + apply to seats | Full access |
 */

/** Free registered users may send this many messages across all Wil chats. */
export const FREE_WIL_MESSAGE_LIMIT = process.env.NEXT_PUBLIC_DEPLOYMENT_ENV === 'production' ? 50 : 5

/** Product that unlocks unlimited Wil chat ($49 Tools). */
export const WIL_AI_UPGRADE_PRODUCT_ID = 'scom-accelerator-tools'

/** Product that unlocks human Startups.com team advisors ($299). */
export const ADVISOR_TEAM_UPGRADE_PRODUCT_ID = 'scom-accelerator-advisor'

/** Platform withhold from the expert on paid Sessions (founder still pays listed). */
export const SESSION_EARNINGS_FEE_FREE_PERCENT = 20
export const SESSION_EARNINGS_FEE_PRO_PERCENT = 15
export const SESSION_EARNINGS_FEE_TEAM_PERCENT = 10

export function resolveAdvisorSessionEarningsFeePercent(feePercent?: number | null) {
  if (feePercent == null) {
    return SESSION_EARNINGS_FEE_FREE_PERCENT
  }
  const n = Number(feePercent)
  return Number.isFinite(n) && n >= 0 && n <= 100 ? n : SESSION_EARNINGS_FEE_FREE_PERCENT
}

export function sessionEarningsKeepPercent(feePercent?: number | null) {
  return 100 - resolveAdvisorSessionEarningsFeePercent(feePercent)
}

/** Nav / profile badges for the Startups.com human team. */
export type TeamAdvisorBadge = 'Lead Advisor' | 'Customers' | 'Pitch Decks' | 'Technology'

/**
 * Suggested team roster shown when the user is not entitled to message humans yet.
 * Order is alphabetical by name (Ed → Ryan → Steve → Wil).
 * `navKey` is used for locked-nav deep links when no engagement row exists yet.
 */
export const LOCKED_TEAM_ADVISORS = [
  {
    navKey: 'ed-kang',
    name: 'Ed Kang',
    badge: 'Lead Advisor' as TeamAdvisorBadge,
    role: 'Chief Strategy Officer',
    bio: 'Full-time advisor — 20 years coaching Founders through the raise.',
    avatarUrl: '/assets/people/ed.jpg',
    category: 'Lead Advisor',
    categoryColor: 'yellow',
    /** Known production user id (board / members profile). */
    userId: '3e095bb1-23b0-4715-ae23-3610e506fe0b',
  },
  {
    navKey: 'ryan-rutan',
    name: 'Ryan Rutan',
    badge: 'Customers' as TeamAdvisorBadge,
    role: 'Chief Marketing Officer',
    bio: 'Go-to-market, growth, and positioning for early-stage Founders.',
    avatarUrl: '/assets/people/ryan.jpg',
    category: 'Growth',
    categoryColor: 'orange',
    userId: '2fe48802-5c59-4c93-b62d-958fc4da3246',
  },
  {
    navKey: 'steve-gacka',
    name: 'Steve Gacka',
    badge: 'Technology' as TeamAdvisorBadge,
    role: 'Chief Technology Officer',
    bio: 'Product, engineering, and technical strategy for early-stage startups.',
    avatarUrl: '/assets/people/gacka.jpg',
    category: 'Engineering',
    categoryColor: 'blue',
    userId: '79e81457-f36a-4a6f-8486-9979b8b249a0',
  },
  {
    navKey: 'wil-schroter',
    name: 'Wil Schroter',
    badge: 'Pitch Decks' as TeamAdvisorBadge,
    role: 'Founder & CEO',
    bio: 'Pitch Decks, funding, and overall strategy for early-stage startups.',
    avatarUrl: WIL_AVATAR,
    category: 'Leadership',
    categoryColor: 'purple',
    userId: '8d0604da-f794-406c-ac42-957fb021663e',
  },
] as const

export type LockedTeamAdvisor = (typeof LOCKED_TEAM_ADVISORS)[number]

/** Role badge for a Startups.com team advisor (by full name). */
export function teamAdvisorBadgeForName(name?: string | null): TeamAdvisorBadge | null {
  if (!name) {
    return null
  }
  const normalized = name.trim().toLowerCase()
  const match = LOCKED_TEAM_ADVISORS.find(a => a.name.toLowerCase() === normalized)
  return match?.badge ?? null
}

/** Look up static team roster entry by navKey, userId, or name. */
export function findLockedTeamAdvisor(params: {
  navKey?: string | null
  userId?: string | null
  name?: string | null
}): LockedTeamAdvisor | null {
  const { navKey, userId, name } = params
  if (navKey) {
    const byKey = LOCKED_TEAM_ADVISORS.find(a => a.navKey === navKey)
    if (byKey) {
      return byKey
    }
  }
  if (userId) {
    const byId = LOCKED_TEAM_ADVISORS.find(a => a.userId && a.userId === userId)
    if (byId) {
      return byId
    }
  }
  if (name) {
    const normalized = name.trim().toLowerCase()
    const byName = LOCKED_TEAM_ADVISORS.find(a => a.name.toLowerCase() === normalized)
    if (byName) {
      return byName
    }
  }
  return null
}

/** Events search URL for a team advisor by full name (e.g. `/events?search=Ed%20Kang`). */
export function teamAdvisorEventsSearchHref(fullName: string): string {
  return `/events?search=${encodeURIComponent(fullName.trim())}`
}

/**
 * Interim default scheduling URL for Startups.com team advisors when
 * `AdvisorProfile.sessionBookingUrl` is not yet set.
 * Prefer profile URLs in product; keep this until team profiles are seeded (ADV-B6).
 * Prefer {@link teamAdvisorEventsSearchHref} per advisor when possible.
 */
export const TEAM_BOOK_MEETING_URL = '/events'

/**
 * Interim per-advisor book-meeting overrides (keyed by {@link LockedTeamAdvisor.navKey}).
 * Used only when `sessionBookingUrl` is empty so deploy does not disable all team CTAs.
 * Advisors not listed fall back to `/events?search={fullName}`.
 */
export const TEAM_ADVISOR_BOOK_MEETING_URLS: Partial<
  Record<(typeof LOCKED_TEAM_ADVISORS)[number]['navKey'], string>
> = {
  // Steve keeps a dedicated event; Wil / Ed / Ryan use name search (see fallback below).
  'steve-gacka': '/events/37cda34f-2fbf-4364-87b3-89c2eaf12acc',
  'ed-kang': teamAdvisorEventsSearchHref('Ed Kang'),
  'ryan-rutan': teamAdvisorEventsSearchHref('Ryan Rutan'),
  'wil-schroter': teamAdvisorEventsSearchHref('Wil Schroter'),
}

/**
 * Book-meeting href for a human advisor (L8 / ADV-B6).
 * Prefers `AdvisorProfile.sessionBookingUrl` when present.
 * Interim: Startups.com team coaches fall back to {@link TEAM_ADVISOR_BOOK_MEETING_URLS} or
 * `/events?search={fullName}` when the profile field is empty (pre-seed deploy safety).
 * Returns null when the CTA should be disabled (or when `isAI` — caller should hide for AI).
 */
export function bookMeetingHrefForAdvisor(params: {
  name?: string | null
  isAI?: boolean
  userId?: string | null
  navKey?: string | null
  /** From AdvisorProfile — preferred source when set. */
  sessionBookingUrl?: string | null
}): string | null {
  // Wil.ai is chat-only — no call booking on the AI profile.
  if (params.isAI) {
    return null
  }

  const fromProfile = params.sessionBookingUrl?.trim()
  if (fromProfile) {
    return fromProfile
  }

  // Interim fallback for team roster only — remove once sessionBookingUrl is seeded for all team coaches.
  const advisor = findLockedTeamAdvisor({
    name: params.name,
    userId: params.userId,
    navKey: params.navKey,
  })
  if (!advisor) {
    return null
  }

  return TEAM_ADVISOR_BOOK_MEETING_URLS[advisor.navKey] || teamAdvisorEventsSearchHref(advisor.name)
}

/** First name for CTAs — "Steve Gacka" → "Steve", "Wil.ai" → "Wil". */
export function advisorFirstName(name?: string | null): string {
  if (!name?.trim()) {
    return 'Advisor'
  }
  const first = name.trim().split(/\s+/)[0]
  return first.replace(/\.[a-z0-9]+$/i, '') || first
}

/** Office-hours CTA (Events / `sessionBookingUrl`). Never use this for Track C paid sessions. */
export function bookMeetingLabelForAdvisor(name?: string | null): string {
  void name
  return 'Office hours'
}

export function useAdvisorsAccess() {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const search = searchParams?.toString() || ''
  const { currentUser, currentUserLoading, currentUserHasContentPermission } = useCurrentUser()

  return useMemo(() => {
    const isGuest = !currentUser || !!currentUser.isGuest
    const isRegistered = !!currentUser?.isRegistered && !currentUser.isGuest
    const canAccessAdvisorTeam =
      !!currentUser?.isAdmin ||
      !!currentUser?.isAdvisor ||
      currentUserHasContentPermission('canAccessAdvisorTeam')

    // Unlimited Wil for anyone on a paid chat-capable tier (Tools $49, Community, Advisor $299, etc.).
    // Free registered accounts have no active contentPermissions for canAccessChat.
    const canAccessUnlimitedWilAi =
      !!currentUser?.isAdmin ||
      canAccessAdvisorTeam ||
      currentUserHasContentPermission('canAccessChat') ||
      !!currentUser?.isFounderCommunityMember ||
      !!currentUser?.isFounderGroupsMember

    /**
     * Paying $49+ tiers (Tools / Community / Advisor / FG). Anyone authenticated
     * may apply; this flag auto-approves the application instead of admin review.
     * Prefer contentPermissions so Tools / Community / Advisor plans all pass without
     * relying only on isFounderCommunityMember (stale denorm or non-FC SKUs).
     */
    const canAccessBecomeAdvisor =
      !!currentUser?.isAdmin ||
      currentUserHasContentPermission('canAccessChat') ||
      currentUserHasContentPermission('canAccessPremiumBenefits') ||
      canAccessAdvisorTeam ||
      !!currentUser?.isFounderCommunityMember ||
      !!currentUser?.isFounderGroupsMember
    const canAutoApproveAdvisorApplication = canAccessBecomeAdvisor

    /**
     * Min $49 plan (Tools / Community / Advisor / FG). Gates marketplace advisor
     * DMs and applying to open seats. Team advisors stay on {@link canAccessAdvisorTeam}.
     * `canAccessChat` is the product contentPermission PaywalledAction reads.
     */
    const canAccessPaidAdvisorTier = canAccessUnlimitedWilAi

    // Tools ($49) and other card-on-file memberships should prorate-upgrade to Advisor ($299),
    // not start a second subscription via the payment form.
    const canProrateTeamUpgrade = canProrateUpgradeToProduct(currentUser, ADVISOR_TEAM_UPGRADE_PRODUCT_ID)
    // Both open the Conversion checkout over the current page; it quotes a prorated change
    // for members on a membership plan and runs the normal checkout for everyone else.
    const teamUpgradeHref = conversionCheckoutHref(pathname || '/advisors/upgrade', search, {
      productId: ADVISOR_TEAM_UPGRADE_PRODUCT_ID,
      intent: 'leadership-message',
    })
    const wilUpgradeHref = conversionCheckoutHref(pathname || '/advisors', search, {
      productId: WIL_AI_UPGRADE_PRODUCT_ID,
    })

    return {
      isGuest,
      isRegistered,
      isLoading: !!currentUserLoading,
      canAccessAdvisorTeam,
      canAccessBecomeAdvisor,
      canAutoApproveAdvisorApplication,
      canAccessPaidAdvisorTier,
      canAccessUnlimitedWilAi,
      freeWilMessageLimit: FREE_WIL_MESSAGE_LIMIT,
      wilUpgradeProductId: WIL_AI_UPGRADE_PRODUCT_ID,
      teamUpgradeProductId: ADVISOR_TEAM_UPGRADE_PRODUCT_ID,
      /** True when team upgrade should change an existing subscription SKU (prorated). */
      canProrateTeamUpgrade,
      wilUpgradeHref,
      teamUpgradeHref,
      /** True when free-tier usage should be enforced (registered, not paid for unlimited AI). */
      isWilMessageLimited: isRegistered && !canAccessUnlimitedWilAi,
    }
  }, [currentUser, currentUserLoading, currentUserHasContentPermission, pathname, search])
}

export function remainingWilMessages(used: number, limit = FREE_WIL_MESSAGE_LIMIT) {
  return Math.max(0, limit - used)
}

export function isWilMessageLimitReached(used: number, limit = FREE_WIL_MESSAGE_LIMIT) {
  return used >= limit
}
